// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The event log.
 *
 * This is a trust surface, not a debug aid. A tool that tells someone their
 * neighbour's payslip contains an earlier version has to be able to show its
 * working, step by step, and this is where it does that: which offset the
 * chain started at, which section it read next, which candidate revision was
 * rejected and why.
 *
 * It is also the only console this tool has — `console.*` is dropped at build
 * time, because these files are payslips and bank statements and a stray debug
 * line would print somebody's salary into the developer tools of a machine
 * that is very often not theirs.
 *
 * The drawer covers most of a phone screen AND hides its own toggle button, so
 * it ships the same four exits the modals do. It is created and removed rather
 * than hidden, so a stale transparent overlay cannot survive to swallow taps.
 */

import type { FindingLevel } from './types.ts';

interface LogLine {
  time: string;
  level: FindingLevel;
  message: string;
}

const MARKS: Record<FindingLevel, string> = { good: '✓', info: '·', warn: '!', bad: '×' };

const lines: LogLine[] = [];
let listEl: HTMLElement | null = null;
let countEl: HTMLElement | null = null;
let backdropEl: HTMLElement | null = null;

function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function render() {
  if (!listEl) return;
  if (lines.length === 0) {
    listEl.innerHTML =
      '<p class="log-empty">Nothing yet. Open a PDF and every step of the reading will appear here.</p>';
    return;
  }
  listEl.replaceChildren(
    ...lines.map((line) => {
      const row = document.createElement('div');
      row.className = 'log-line';
      const t = document.createElement('span');
      t.className = 'log-time';
      t.textContent = line.time;
      const m = document.createElement('span');
      m.className = `log-mark lv-${line.level}`;
      m.textContent = MARKS[line.level];
      const s = document.createElement('span');
      s.className = 'log-msg';
      s.textContent = line.message;
      row.append(t, m, s);
      return row;
    }),
  );
  listEl.scrollTop = listEl.scrollHeight;
}

export function logEvent(level: FindingLevel, message: string) {
  lines.push({ time: stamp(), level, message });
  if (lines.length > 600) lines.splice(0, lines.length - 600);
  if (countEl) countEl.textContent = String(lines.length);
  render();
}

export function clearLog() {
  lines.length = 0;
  if (countEl) countEl.textContent = '0';
  render();
}

export function logText(): string {
  return lines.map((l) => `[${l.time}] ${MARKS[l.level]} ${l.message}`).join('\n');
}

export function closeDrawer() {
  if (!backdropEl) return;
  document.removeEventListener('keydown', onKey, true);
  backdropEl.remove();
  backdropEl = null;
  listEl = null;
  document.getElementById('log-toggle')?.setAttribute('aria-expanded', 'false');
}

function onKey(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.stopPropagation();
    closeDrawer();
  }
}

export function openDrawer() {
  if (backdropEl) return;
  const host = document.getElementById('modal-host');
  if (!host) return;

  const backdrop = document.createElement('div');
  backdrop.className = 'drawer-backdrop';

  const drawer = document.createElement('aside');
  drawer.className = 'drawer';
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-modal', 'true');
  drawer.setAttribute('aria-label', 'Event log');

  const head = document.createElement('div');
  head.className = 'drawer-head';
  const title = document.createElement('h2');
  title.className = 'panel-title';
  title.textContent = 'Event log';

  const close = document.createElement('button');
  close.className = 'icon-btn';
  close.type = 'button';
  close.setAttribute('aria-label', 'Close the event log');
  close.innerHTML =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';

  head.append(title, close);

  const list = document.createElement('div');
  list.className = 'drawer-log';
  drawer.append(head, list);
  backdrop.append(drawer);

  // The drawer sits inside the backdrop, so this one handler covers every
  // point outside the panel — see modal.ts for why pointerdown.
  backdrop.addEventListener('pointerdown', (event) => {
    if (!drawer.contains(event.target as Node)) closeDrawer();
  });
  close.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeDrawer();
  });
  close.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closeDrawer();
    }
  });
  document.addEventListener('keydown', onKey, true);

  host.append(backdrop);
  backdropEl = backdrop;
  listEl = list;
  render();
  document.getElementById('log-toggle')?.setAttribute('aria-expanded', 'true');
  close.focus();
}

export function bindLogToggle(button: HTMLElement, counter: HTMLElement) {
  countEl = counter;
  countEl.textContent = String(lines.length);
  button.addEventListener('click', () => {
    if (backdropEl) closeDrawer();
    else openDrawer();
  });
}
